import { z } from 'zod';

/**
 * I18n Schema Definitions
 * 
 * Centralized Zod schemas for internationalization types
 */

// Supported locales schema
export const SupportedLocaleSchema = z.enum(['en', 'tr']);
export type SupportedLocale = z.infer<typeof SupportedLocaleSchema>;

// Translation parameters schema
export const TranslationParamsSchema = z.record(z.string(), z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.undefined()
]));
export type TranslationParams = z.infer<typeof TranslationParamsSchema>;

// Plural options schema
export const PluralOptionsSchema = z.object({
  count: z.number().optional(),
  ordinal: z.boolean().optional()
});
export type PluralOptions = z.infer<typeof PluralOptionsSchema>;

// I18n configuration schema
export const I18nConfigSchema = z.object({
  defaultLocale: SupportedLocaleSchema,
  supportedLocales: z.array(SupportedLocaleSchema),
  fallbackLocale: SupportedLocaleSchema,
  debug: z.boolean().optional(),
  namespaces: z.array(z.string()).optional(),
  defaultNamespace: z.string().optional()
});
export type I18nConfig = z.infer<typeof I18nConfigSchema>;

// Translation options schema
export const TranslationOptionsSchema = z.object({
  locale: SupportedLocaleSchema.optional(),
  namespace: z.string().optional(),
  params: TranslationParamsSchema.optional(),
  plural: PluralOptionsSchema.optional(),
  defaultValue: z.string().optional()
});
export type TranslationOptions = z.infer<typeof TranslationOptionsSchema>;

// Translation key schema (enforces dot notation)
export const TranslationKeySchema = z.string().min(1).refine(
  (key) => /^[a-zA-Z][a-zA-Z0-9]*(\.[a-zA-Z][a-zA-Z0-9]*)*$/.test(key),
  { message: "Translation key must use dot notation (e.g., 'app.name', 'messages.welcome')" }
);
export type TranslationKey = z.infer<typeof TranslationKeySchema>;

// Namespace schema
export const I18nNamespaceSchema = z.enum(['common', 'canvas', 'admin', 'errors', 'emails']);
export type I18nNamespace = z.infer<typeof I18nNamespaceSchema>;

// Date format schema
export const DateFormatSchema = z.enum(['short', 'medium', 'long', 'full']);
export type DateFormat = z.infer<typeof DateFormatSchema>;

// Text direction schema
export const TextDirectionSchema = z.enum(['ltr', 'rtl']);
export type TextDirection = z.infer<typeof TextDirectionSchema>;

// Translation entry schema (for database storage)
export const TranslationEntrySchema = z.object({
  id: z.string().uuid().optional(),
  key: TranslationKeySchema,
  namespace: I18nNamespaceSchema,
  locale: SupportedLocaleSchema,
  value: z.string(),
  description: z.string().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional()
});
export type TranslationEntry = z.infer<typeof TranslationEntrySchema>;

// Translation bundle schema (for namespace translations)
export const TranslationBundleSchema = z.object({
  namespace: I18nNamespaceSchema,
  locale: SupportedLocaleSchema,
  translations: z.record(z.string(), z.any()) // Allow nested objects
});
export type TranslationBundle = z.infer<typeof TranslationBundleSchema>;

// Locale info schema
export const LocaleInfoSchema = z.object({
  code: SupportedLocaleSchema,
  name: z.string(),
  nativeName: z.string(),
  direction: TextDirectionSchema,
  currency: z.string().optional(),
  dateFormat: z.string().optional(),
  enabled: z.boolean().default(true)
});
export type LocaleInfo = z.infer<typeof LocaleInfoSchema>;

// Validation helpers
export const validateTranslationKey = (key: string): boolean => {
  return TranslationKeySchema.safeParse(key).success;
};

export const validateLocale = (locale: string): locale is SupportedLocale => {
  return SupportedLocaleSchema.safeParse(locale).success;
};

export const validateNamespace = (namespace: string): namespace is I18nNamespace => {
  return I18nNamespaceSchema.safeParse(namespace).success;
};

/**
 * Parse and validate locale from string, returning valid locale or fallback
 * @param localeString - String to parse as locale
 * @param fallback - Fallback locale if invalid (default: 'en')
 * @returns Valid SupportedLocale
 */
export const parseLocaleOrFallback = (localeString: string | undefined, fallback: SupportedLocale = 'en'): SupportedLocale => {
  if (!localeString) return fallback;
  return validateLocale(localeString) ? localeString : fallback;
};

/**
 * Get first valid locale from Accept-Language header
 * @param acceptLanguage - Accept-Language header value
 * @param fallback - Fallback locale (default: 'en')
 * @returns Valid SupportedLocale
 */
export const parseAcceptLanguage = (acceptLanguage: string | null, fallback: SupportedLocale = 'en'): SupportedLocale => {
  if (!acceptLanguage) return fallback;

  const languages = acceptLanguage.split(',');
  for (const lang of languages) {
    const locale = lang.split('-')[0]?.trim();
    if (locale && validateLocale(locale)) {
      return locale;
    }
  }
  
  return fallback;
};

/**
 * Get locale info by code
 * @param locale - Locale code
 * @returns LocaleInfo object or undefined
 */
export const getLocaleInfo = (locale: SupportedLocale): LocaleInfo | undefined => {
  return DEFAULT_LOCALES.find(info => info.code === locale);
};

/**
 * Get text direction for locale
 * @param locale - Locale code
 * @returns Text direction ('ltr' or 'rtl')
 */
export const getTextDirection = (locale: SupportedLocale): TextDirection => {
  const info = getLocaleInfo(locale);
  return info?.direction || 'ltr';
};

/**
 * Check if locale is RTL
 * @param locale - Locale code
 * @returns True if RTL locale
 */
export const isRTLLocale = (locale: SupportedLocale): boolean => {
  return getTextDirection(locale) === 'rtl';
};

// Default locale configurations
export const DEFAULT_LOCALES: LocaleInfo[] = [
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    direction: 'ltr',
    currency: 'USD',
    dateFormat: 'MM/dd/yyyy',
    enabled: true
  },
  {
    code: 'tr',
    name: 'Turkish',
    nativeName: 'Türkçe',
    direction: 'ltr',
    currency: 'TRY',
    dateFormat: 'dd.MM.yyyy',
    enabled: true
  }
];

// Supported locales array (DRY principle - single source of truth)
export const SUPPORTED_LOCALES: SupportedLocale[] = ['en', 'tr'];

// Supported namespaces array (DRY principle)
export const SUPPORTED_NAMESPACES: I18nNamespace[] = ['common', 'canvas', 'admin', 'errors', 'emails'];

// Default i18n configuration
export const DEFAULT_I18N_CONFIG: I18nConfig = {
  defaultLocale: 'en',
  supportedLocales: SUPPORTED_LOCALES,
  fallbackLocale: 'en',
  debug: false,
  namespaces: SUPPORTED_NAMESPACES.slice(0, 3), // Only load common, canvas, admin by default
  defaultNamespace: 'common'
};